import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import MarkdownRenderer from '../components/MarkdownRenderer';
import ImageUpload from '../components/ImageUpload';
import './CreatePost.css';

const CreatePost = () => {
  const navigate = useNavigate();
  const { isAdmin, loading } = useAuth();
  const contentTextareaRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    tags: []
  });
  const [loadingState, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    // 如果不是管理员，重定向到首页
    if (!loading && !isAdmin) {
      alert('只有管理员才能创建文章');
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTagsChange = (e) => {
    const tagsString = e.target.value;
    const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData({
      ...formData,
      tags: tagsArray
    });
  };

  const handleImageUpload = (imageMarkdown) => {
    if (!isAdmin) {
      alert('只有管理员才能上传图片');
      return;
    }

    // 在光标位置插入图片Markdown
    const textarea = contentTextareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const content = formData.content;
    const newContent = content.substring(0, start) + '\n' + imageMarkdown + '\n' + content.substring(end);
    
    setFormData({
      ...formData,
      content: newContent
    });
    
    // 设置光标位置到插入的图片后面
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + imageMarkdown.length + 2, start + imageMarkdown.length + 2);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAdmin) {
      alert('只有管理员才能创建文章');
      return;
    }
    
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('标题和内容不能为空');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/posts', formData);
      navigate(`/post/${response.data.id}`);
    } catch (error) {
      if (error.response?.status === 401) {
        alert('权限不足，请先登录管理员账户');
      } else {
        setError('创建文章失败: ' + (error.response?.data?.error || '未知错误'));
      }
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  // 如果正在加载认证状态或不是管理员，显示相应信息
  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="create-post">
        <div className="container">
          <div className="access-denied">
            <h1>访问受限</h1>
            <p>只有管理员才能创建文章。</p>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              返回首页
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-post">
      <div className="container">
        <div className="form-container">
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleSubmit} className="post-form">
            <div className="form-group">
              <label htmlFor="title">文章标题 *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="请输入文章标题"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="author">作者</label>
              <input
                type="text"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                placeholder="请输入作者名称（可选）"
              />
            </div>

            <div className="form-group">
              <label htmlFor="tags">标签</label>
              <input
                type="text"
                id="tags"
                value={formData.tags.join(', ')}
                onChange={handleTagsChange}
                placeholder="请输入标签，用逗号分隔（可选）"
              />
            </div>

            <div className="form-group">
              <div className="content-header">
                <label htmlFor="content">文章内容 *</label>
                <div className="content-toolbar">
                  <button 
                    type="button"
                    onClick={togglePreview}
                    className={`btn btn-outline btn-small ${showPreview ? 'active' : ''}`}
                    disabled={loadingState}
                  >
                    {showPreview ? '编辑模式' : 'Markdown 预览'}
                  </button>
                </div>
              </div>
              
              <div className="markdown-hints">
                <strong>Markdown 格式提示:</strong>
                <span># 标题</span>
                <span>**粗体**</span>
                <span>*斜体*</span>
                <span>`代码`</span>
                <span>[链接](url)</span>
                <span>> 引用</span>
                <span>- 列表</span>
                <span>![图片](url)</span>
              </div>

              <div className="image-style-hints">
                <strong>图片样式语法:</strong>
                <span>![描述|size:small](url) - 小图</span>
                <span>![描述|width:300px](url) - 自定义宽度</span>
                <span>![描述|radius:10px](url) - 圆角</span>
                <span>![描述|round](url) - 圆形</span>
                <span>![描述|shadow](url) - 阴影</span>
                <span>![描述|size:medium|round|shadow](url) - 组合样式</span>
              </div>

              {/* 图片上传组件 - 启用粘贴功能 */}
              <ImageUpload 
                onImageUpload={handleImageUpload} 
                enablePaste={true}
                targetElementRef={contentTextareaRef}
              />

              <div className="content-wrapper">
                {showPreview ? (
                  <div className="preview-content">
                    <h3>预览效果:</h3>
                    {formData.content ? (
                      <MarkdownRenderer content={formData.content} />
                    ) : (
                      <p className="preview-empty">开始输入内容以查看预览效果...</p>
                    )}
                  </div>
                ) : (
                  <textarea
                    ref={contentTextareaRef}
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    className="content-input"
                    placeholder="请输入文章内容，支持 Markdown 格式&#10;&#10;示例：&#10;# 一级标题&#10;## 二级标题&#10;&#10;这是一个段落。**这是粗体文本**，*这是斜体文本*。&#10;&#10;> 这是一个引用&#10;&#10;- 这是列表项目 1&#10;- 这是列表项目 2&#10;&#10;```javascript&#10;console.log('这是代码块');&#10;```&#10;&#10;这是一个 [链接示例](https://example.com)。&#10;&#10;![图片描述](图片URL)&#10;&#10;🎨 图片样式示例：&#10;![小图|size:small](图片URL)&#10;![中等大小圆形图片|size:medium|round](图片URL)&#10;![自定义宽度带阴影|width:400px|shadow](图片URL)&#10;![圆角图片|radius:15px](图片URL)&#10;&#10;💡 提示：在此编辑区域按 Ctrl+V 可直接粘贴剪贴板中的图片"
                    rows="15"
                    required
                  />
                )}
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loadingState}
              >
                {loadingState ? '发布中...' : '发布文章'}
              </button>
              <button 
                type="button" 
                onClick={handleCancel}
                className="btn btn-outline"
                disabled={loadingState}
              >
                取消
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePost;